import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import MessageCard from './MessageCard';
import { IMessage } from 'models';
import { debounce, filter } from 'lodash';
import { createChat, getChatsUsingRoomId } from 'store/reducers/chat';
import { socketContextType } from 'index';
import SocketContext from 'socket-context';
import { UserInfoMapperSocketHandler } from 'utils/UserInfoMapper';
import { validateAndCreateSocketIfExist } from 'utils/SocketValidator';
import moment from 'moment';
import ChatInputs from './ChatInputs';
import { createSocketMessage } from 'middlewares/transform/chat';
import { User } from 'models/User';
import { chatParticipantType } from 'types/Constants';
import axiosInstance from 'config/axios';

const CommonChat = (props: any) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { chats } = useSelector((state: RootState) => state.live);
  const { user } = useSelector((state: RootState) => state.auth);
  const [searchQuery, setSearchQuery] = useState('');
  const [previousMessageCount, setPreviousMessageCount] = useState(0);
  const [replyTo, setReplyTo] = useState<IMessage | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const roomId = useSelector((state: RootState) => state.live.settings.roomId);
  let userInfoHandler = new UserInfoMapperSocketHandler();
  const socketList: socketContextType[] = useContext(SocketContext);

  useEffect(() => {
    userInfoHandler = validateAndCreateSocketIfExist(roomId, handleOnMessage, socketList);
    dispatch(getChatsUsingRoomId({ room_id: roomId, session_map_id: roomId }));
  }, []);

  const handleOnMessage = async (data: any) => {
    try {
      let _data = JSON.parse(data);
      if (_data?.type === "chat_message") {
        // Check if the message is an image URL and update message_type
        if (/\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(_data.message.text)) {
          _data.message.message_type = 'attachment';
        }
        dispatch(getChatsUsingRoomId({ room_id: roomId, session_map_id: roomId }));
      }
    } catch (error) {
      console.error('Error parsing socket message:', error);
    }
  };

  const sendMessage = async (strMessageType: any, newMessage: any) => {
    let objBody = {
      type: strMessageType,
      session_map_id: roomId,
      room_id: roomId,
      message_by_id: user?.id,
      message_to_id: 0,
      user_message_data: JSON.stringify(newMessage),
    };
    try {
      const res: any = await dispatch(createChat(objBody));
      if (res && typeof res === "object") {
        let _msg = {
          type: "chat_message",
          message: newMessage,
          room_id: roomId,
          sub_type: "class",
          chat_box_id: roomId,
        };
        userInfoHandler = validateAndCreateSocketIfExist(roomId, () => {}, socketList);
        userInfoHandler.sendMessage(JSON.stringify(_msg));
        saveActions();
        dispatch(getChatsUsingRoomId({ room_id: roomId, session_map_id: roomId }));
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const saveActions = async () => {
    const session = await axiosInstance.post("/trainee/live_session_plan_preview_trainee", {
      session_map_id: roomId,
    });

    const responseData = session?.data?.responseData || {};
    const allActivities = [
      ...(responseData.prePlannedActivities || []),
      ...(responseData.quickSetupActivities || []),
    ];

    const launchData = allActivities.find((el: any) => el.status === "LAUNCH");
    if (!launchData?.id) {
      return;
    }

    const engagementType = "L_CHAT";
    const obj = {
      engagementType,
      userId: user?.id,
      sessionActivityExecutionLogId: launchData?.id,
      isRead: "NO",
      participantId: user?.id,
      subType: null,
    };

    await axiosInstance.post("/session/save_session_actions_log", obj);
  };

  const handleSockets = async (message: any, replyTo?: any) => {
    if (!message.trim()) {
      return;
    }

    // Function to check if the message is an image URL
    const isImageUrl = (text: string) => {
      return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(text);
    };

    if (replyTo !== null) {
      const replyToz = {
        ...replyTo,
        message_by_id: user?.id,
        id: replyTo.author.id,
        type: 'chat_message',
        room_id: roomId,
        message_by_type: null,
        created_at: moment().format('hh:mm a'),
        session_map_id: roomId,
        user_message_data: JSON.stringify(replyTo),
      };

      const msgObj = {
        text: message,
        author: {
          id: user?.id,
          name: user?.name,
        },
        message_type: isImageUrl(message) ? 'attachment' : 'text',
        message_to_id: replyTo.author.id,
        message_time: moment().format('hh:mm a'),
        message_reply_to: replyToz,
        sender_image: user?.profileImage,
        isPrivate: false,
      };

      await sendMessage('chat_message', msgObj);
      return;
    }

    const objUserReactionData = {
      message: message,
      message_by: chatParticipantType?.PARTICIPANT,
      message_by_name: user?.name,
      message_by_id: user?.id,
      message_type: isImageUrl(message) ? 'attachment' : 'text',
      message_to: chatParticipantType?.PARTICIPANT,
      message_to_id: 0,
      message_time: moment(new Date()).format("hh:mm a"),
      message_reply_to: '',
      sender_image: user?.profileImage,
    };

    await sendMessage('chat_message', objUserReactionData);
  };

  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  };

  const handleSearch = debounce((value: string) => setSearchQuery(value), 300);

  const filteredMessages: any = searchQuery
    ? chats.messages.filter((msg) => !msg.isPrivate && msg.text.toLowerCase().includes(searchQuery.toLowerCase()))
    : chats.messages.filter((msg) => !msg.isPrivate);

  return (
    <div className="flex flex-col flex-1 bg-[#38393F] h-full">
      <div className="sticky top-[] z-10 p-2 m-2 rounded-[4px] border border-[#3B4350] bg-[#24272C] shadow-[inset_-5px_-5px_20px_rgba(255,255,255,0.25),inset_5px_5px_20px_rgba(0,0,0,0.25)]">
        <div className="flex items-center">
          <input
            type="text"
            className="flex-1 outline-none text-gray-200 placeholder-[#D4D6DB] bg-transparent"
            placeholder="Search messages..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
          />
          <svg
            className="w-5 h-5 text-gray-400 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>
      <div className="flex flex-col flex-1 overflow-y-auto scrollbar-hide mobile:max-h-[15%] laptop:max-h-[67vh] desktop:max-h-[65vh] largescreen:max-h-[70vh]" ref={containerRef}>
        {filteredMessages.length > 0 && filteredMessages.map((message, index) => (
          <MessageCard
            key={index}
            isReceived={message.author?.id ? message.author.id !== user?.id : false}
            message={message}
            isPrivateChat={false}
            setReplyTo={() => setReplyTo(message)}
          />
        ))}
      </div>
      <div className="absolute inset-x-0 bottom-0">
        <ChatInputs onMessage={handleSockets} />
      </div>
    </div>
  );
};

export default CommonChat;
